/**
 * 时间检查工具
 * 用于检查当前时间是否在工作时间内
 * 优先使用交易时间接口返回的信息，如果接口不可用则从 config.js 配置文件中读取
 */

// 导入配置
import { WORKING_TIME } from '../config.js'
// 导入交易时间服务
import { checkTradingTime as checkTradingTimeFromService, getTradingStatus } from './tradingTimeService.js'

// 缓存接口返回的状态（用于同步函数）
let cachedTradingStatus = null
let lastCacheUpdate = 0
// 默认缓存更新间隔：1分钟（非价格页面）
const DEFAULT_CACHE_UPDATE_INTERVAL = 60 * 1000
// 价格页面专用缓存更新间隔：30分钟
const PRICE_CACHE_UPDATE_INTERVAL = 30 * 60 * 1000

/**
 * 获取工作时间配置
 * @returns {Object} 工作时间配置对象
 */
function getWorkingTimeConfig() {
  return WORKING_TIME
}

/**
 * 异步更新交易时间状态缓存
 */
async function updateTradingStatusCache() {
  try {
    const status = await getTradingStatus()
    cachedTradingStatus = {
      isWorkingTime: status.open,
      message: status.description
    }
    lastCacheUpdate = Date.now()
  } catch (error) {
    // 如果接口调用失败，使用配置文件的值
    console.warn('更新交易时间状态缓存失败，使用配置文件:', error)
  }
}

/**
 * 初始化时更新一次缓存
 * - 价格页面（/price/index, /price/tv 及其子路径）：30 分钟调用一次 /app/trading/status
 * - 其他页面：1 分钟调用一次
 */
if (typeof window !== 'undefined') {
  const path = window.location && window.location.pathname ? window.location.pathname : ''
  const isPricePage = path === '/price/index' || path === '/price/member' || path === '/price/tv' || path.startsWith('/price/tv/')
  const interval = isPricePage ? PRICE_CACHE_UPDATE_INTERVAL : DEFAULT_CACHE_UPDATE_INTERVAL

  updateTradingStatusCache()
  setInterval(updateTradingStatusCache, interval)
}

/**
 * 检查当前时间是否在工作时间内（异步版本，优先使用接口）
 * @param {Object} options - 可选参数
 * @param {boolean} options.testMode - 是否为测试模式
 * @param {number} options.testHour - 测试模式下的强制小时（0-23）
 * @param {number} options.testMinute - 测试模式下的强制分钟（0-59）
 * @param {number} options.testDay - 测试模式下的强制星期（0-6, 0是周日）
 * @returns {Promise<Object>} 返回检查结果 {isWorkingTime: boolean, message: string, currentTime?: string}
 */
export async function checkWorkingTimeAsync(options = {}) {
  const now = new Date()
  
  // 如果是测试模式，使用配置文件逻辑
  if (options.testMode) {
    return checkWorkingTime(options)
  }
  
  try {
    // 优先使用接口返回的信息
    const result = await checkTradingTimeFromService()
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    
    return {
      isWorkingTime: result.isTradingTime,
      message: result.message,
      currentTime: currentTime
    }
  } catch (error) {
    // 如果接口调用失败，降级使用配置文件
    console.warn('调用交易时间接口失败，使用配置文件:', error)
    return checkWorkingTime(options)
  }
}

/**
 * 检查当前时间是否在工作时间内（同步版本，使用缓存）
 * @param {Object} options - 可选参数
 * @param {boolean} options.testMode - 是否为测试模式
 * @param {number} options.testHour - 测试模式下的强制小时（0-23）
 * @param {number} options.testMinute - 测试模式下的强制分钟（0-59）
 * @param {number} options.testDay - 测试模式下的强制星期（0-6, 0是周日）
 * @returns {Object} 返回检查结果 {isWorkingTime: boolean, message: string, currentTime?: string}
 */
export function checkWorkingTime(options = {}) {
  const now = new Date()
  
  // 如果是测试模式，使用配置文件逻辑
  if (options.testMode) {
    return checkWorkingTimeFromConfig(options)
  }
  
  // 如果有缓存的接口数据且缓存未过期，优先使用
  const nowTs = Date.now()
  const maxAge = (typeof window !== 'undefined' && window.location && window.location.pathname &&
    (window.location.pathname === '/price/index' || window.location.pathname === '/price/tv' || window.location.pathname.startsWith('/price/tv/')))
      ? PRICE_CACHE_UPDATE_INTERVAL
      : DEFAULT_CACHE_UPDATE_INTERVAL

  if (cachedTradingStatus && (nowTs - lastCacheUpdate < maxAge)) {
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    return {
      isWorkingTime: cachedTradingStatus.isWorkingTime,
      message: cachedTradingStatus.message,
      currentTime: currentTime
    }
  }
  
  // 否则使用配置文件逻辑
  return checkWorkingTimeFromConfig(options)
}

/**
 * 从配置文件检查工作时间（内部函数）
 * @param {Object} options - 可选参数
 * @returns {Object} 返回检查结果 {isWorkingTime: boolean, message: string, currentTime?: string}
 */
function checkWorkingTimeFromConfig(options = {}) {
  const now = new Date()
  
  // 从配置文件获取工作时间配置
  const workingTimeConfig = getWorkingTimeConfig()
  const workingDays = workingTimeConfig.WORKING_DAYS
  const workingStartHour = workingTimeConfig.WORKING_START_HOUR
  const workingStartMinute = workingTimeConfig.WORKING_START_MINUTE ?? 0
  const workingEndHour = workingTimeConfig.WORKING_END_HOUR
  const workingEndMinute = workingTimeConfig.WORKING_END_MINUTE ?? 0
  const workingTimeMessage = workingTimeConfig.WORKING_TIME_MESSAGE
  
  // 如果是测试模式，使用测试时间
  let dayOfWeek, hour, minute
  
  if (options.testMode) {
    dayOfWeek = options.testDay !== undefined ? options.testDay : now.getDay()
    hour = options.testHour !== undefined ? options.testHour : now.getHours()
    minute = options.testMinute !== undefined ? options.testMinute : now.getMinutes()
  } else {
    dayOfWeek = now.getDay() // 0-6, 0是周日
    hour = now.getHours()
    minute = now.getMinutes()
  }
  
  const currentTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  
  // 检查是否是工作日
  if (!workingDays.includes(dayOfWeek)) {
    return {
      isWorkingTime: false,
      message: `很抱歉，系统在下班时间不能下单。下单时间：${workingTimeMessage}`,
      currentTime: currentTime
    }
  }
  
  const currentTotalMinutes = hour * 60 + minute
  const startTotalMinutes = workingStartHour * 60 + workingStartMinute
  const endTotalMinutes = workingEndHour * 60 + workingEndMinute

  // 检查时间是否在工作时间内
  if (currentTotalMinutes < startTotalMinutes || currentTotalMinutes > endTotalMinutes) {
    return {
      isWorkingTime: false,
      message: `很抱歉，系统在下班时间不能下单。下单时间：${workingTimeMessage}`,
      currentTime: currentTime
    }
  }
  
  return {
    isWorkingTime: true,
    message: `系统服务时间：${workingTimeMessage}`,
    currentTime: currentTime
  }
}

/**
 * 格式化工作时间提示信息
 * @returns {string} 格式化的时间信息
 */
export function getWorkingTimeDescription() {
  const workingTimeConfig = getWorkingTimeConfig()
  return `服务时间：${workingTimeConfig.WORKING_TIME_MESSAGE}`
}

/**
 * 获取当前时间的友好显示
 * @returns {string} 当前时间的友好格式
 */
export function getCurrentTimeString() {
  const now = new Date()
  const dayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  const dayName = dayNames[now.getDay()]
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  
  return `${dayName} ${time}`
}
